import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Card, Tabs, TabItem, Badge, Button, Tooltip } from 'flowbite-react';
import { useSnackbar } from 'notistack';
import * as sched from 'sched';
import { WorkspaceContext, useWorkspace } from '../../context/WorkspaceContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { FiCheck, FiInfo } from 'react-icons/fi';
import { Link } from 'react-router-dom';

// Helpers
function describeCommand(cmd: sched.DatabaseCommand): string {
  switch (cmd.type) {
    case 'CreateItem':
      return `Create ${cmd.item.type} (${cmd.id})`;
    case 'DeleteItem':
      return `Delete ${cmd.id.type_} (${cmd.id.id})`;
    case 'SetDependency':
      return `Set dependency (${cmd.enabled ? 'enable' : 'disable'})`;
    case 'SetPriority':
      return `Set priority (${cmd.enabled ? 'enable' : 'disable'})`;
    case 'SetWorkMatch':
      return `Set work match (${cmd.enabled ? 'enable' : 'disable'})`;
    case 'SetTeamMembership':
      return `Set team membership (${cmd.enabled ? 'enable' : 'disable'})`;
    case 'UpdateOutcome':
      return `Update Outcome ${cmd.id}`;
    case 'UpdatePerson':
      return `Update Person ${cmd.id}`;
    case 'UpdateProject':
      return `Update Project ${cmd.id}`;
    case 'UpdateTask':
      return `Update Task ${cmd.id}`;
    case 'UpdateTeam':
      return `Update Team ${cmd.id}`;
    case 'UpdateDefaultAvailability':
      return `Update Default Availability`;
    case 'DeleteTaskResourceAllocation':
      return `Delete Task Allocation ${cmd.id}`;
    case 'IncrementTaskResourceAllocation':
      return `Increment Resource Allocation on Task ${cmd.task_id}`;
    default:
      return 'Command';
  }
}

const TidyPage: React.FC = () => {
  const { safeData, workspaceId } = useContext(WorkspaceContext);
  const { buildUrl } = useWorkspace();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actions, setActions] = useState<sched.TidyActions | null>(null);

  // Selection state per tab
  // Duplicates selection is per-item, so we track selected and applied by NodeId key "type_:id"
  const [selectedDuplicateKeys, setSelectedDuplicateKeys] = useState<Set<string>>(new Set());
  const [appliedDuplicateKeys, setAppliedDuplicateKeys] = useState<Set<string>>(new Set());

  const [selectedCleanup, setSelectedCleanup] = useState<boolean[]>([]);
  const [appliedCleanup, setAppliedCleanup] = useState<boolean[]>([]);
  // Virtualization state for cleanup list
  const rowHeight = 56; // px per row
  const [listHeight, setListHeight] = useState<number>(Math.max(200, Math.floor(window.innerHeight * 0.6)));
  const [scrollTop, setScrollTop] = useState<number>(0);

  // Load tidy actions
  useEffect(() => {
    const load = async () => {
      if (!safeData) return;
      try {
        setLoading(true);
        setError(null);
        const resp = await sched.get_tidy_actions(safeData);
        setActions(resp);
        // initialize selections to empty for duplicates
        setSelectedDuplicateKeys(new Set());
        setAppliedDuplicateKeys(new Set());
        setSelectedCleanup(new Array(resp.remove_completed_items.length).fill(true));
        setAppliedCleanup(new Array(resp.remove_completed_items.length).fill(false));
      } catch (e) {
        console.error(e);
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [safeData]);

  const deleteSelectedDuplicates = async () => {
    if (!safeData || !actions) return;
    const commands: sched.DatabaseCommand[] = [];
    const toApplyKeys: string[] = [];
    for (const group of actions.duplicate_items) {
      for (const id of group.items.ids) {
        const key = `${id.type_}:${id.id}`;
        if (selectedDuplicateKeys.has(key) && !appliedDuplicateKeys.has(key)) {
          commands.push({ type: 'DeleteItem', id });
          toApplyKeys.push(key);
        }
      }
    }
    if (commands.length === 0) return;
    try {
      await sched.DataManager.apply_commands(safeData, commands);
      setAppliedDuplicateKeys(prev => {
        const next = new Set(prev);
        toApplyKeys.forEach(k => next.add(k));
        return next;
      });
      setSelectedDuplicateKeys(prev => {
        const next = new Set(prev);
        toApplyKeys.forEach(k => next.delete(k));
        return next;
      });
      enqueueSnackbar(`Deleted ${commands.length} item${commands.length === 1 ? '' : 's'}`, { variant: 'success' });
    } catch (e) {
      console.error('Failed to delete items', e);
      enqueueSnackbar(`Failed to delete items: ${e instanceof Error ? e.message : String(e)}`, { variant: 'error' });
    }
  };

  const duplicateCount = actions?.duplicate_items.length ?? 0;
  const cleanupCount = actions?.remove_completed_items.length ?? 0;

  // Duplicates selection helpers
  const duplicateAllKeys: string[] = useMemo(() => {
    if (!actions) return [];
    const keys: string[] = [];
    for (const group of actions.duplicate_items) {
      for (const id of group.items.ids) {
        keys.push(`${id.type_}:${id.id}`);
      }
    }
    return keys;
  }, [actions]);

  const duplicateSelectedCount = selectedDuplicateKeys.size;
  const duplicateEligibleCount = useMemo(() => duplicateAllKeys.filter(k => !appliedDuplicateKeys.has(k)).length, [duplicateAllKeys, appliedDuplicateKeys]);
  const duplicateAllSelected = useMemo(() => duplicateEligibleCount > 0 && duplicateAllKeys.filter(k => !appliedDuplicateKeys.has(k)).every(k => selectedDuplicateKeys.has(k)), [duplicateAllKeys, selectedDuplicateKeys, appliedDuplicateKeys, duplicateEligibleCount]);
  const duplicateSomeSelected = useMemo(() => duplicateEligibleCount > 0 && !duplicateAllSelected && duplicateAllKeys.some(k => selectedDuplicateKeys.has(k) && !appliedDuplicateKeys.has(k)), [duplicateAllKeys, selectedDuplicateKeys, appliedDuplicateKeys, duplicateAllSelected, duplicateEligibleCount]);

  const cleanupAllSelected = useMemo(() => selectedCleanup.length > 0 && selectedCleanup.every((v, i) => v && !appliedCleanup[i]), [selectedCleanup, appliedCleanup]);
  const cleanupSomeSelected = useMemo(() => {
    const eligible = selectedCleanup.map((v, i) => v && !appliedCleanup[i]);
    return eligible.some(Boolean) && !cleanupAllSelected;
  }, [selectedCleanup, appliedCleanup, cleanupAllSelected]);

  const cleanupSelectedCount = selectedCleanup.reduce((acc, v, i) => acc + (v && !appliedCleanup[i] ? 1 : 0), 0);

  // Handle resize to keep listHeight responsive
  useEffect(() => {
    const onResize = () => setListHeight(Math.max(200, Math.floor(window.innerHeight * 0.6)));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const toggleAllDuplicate = (checked: boolean) => {
    setSelectedDuplicateKeys(prev => {
      const next = new Set<string>(prev);
      duplicateAllKeys.forEach(k => {
        if (appliedDuplicateKeys.has(k)) return;
        if (checked) next.add(k); else next.delete(k);
      });
      return next;
    });
  };
  const toggleDuplicateItem = (key: string) => {
    setSelectedDuplicateKeys(prev => {
      const next = new Set<string>(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleAllCleanup = (checked: boolean) => {
    setSelectedCleanup(selectedCleanup.map((_, i) => (appliedCleanup[i] ? false : checked)));
  };
  const toggleCleanupRow = (index: number) => {
    setSelectedCleanup(prev => prev.map((v, i) => (i === index ? !v : v)));
  };

  const applyCleanup = async () => {
    if (!actions || !safeData) return;

    const commands: sched.DatabaseCommand[] = [];
    const toApplyIdx: number[] = [];

    actions.remove_completed_items.forEach((a, idx) => {
      if (selectedCleanup[idx] && !appliedCleanup[idx]) {
        commands.push(a.suggested_action);
        toApplyIdx.push(idx);
      }
    });

    if (commands.length === 0) return;

    try {
      await sched.DataManager.apply_commands(safeData, commands);
      // mark applied
      setAppliedCleanup(prev => prev.map((v, i) => (toApplyIdx.includes(i) ? true : v)));
      // unselect applied rows
      setSelectedCleanup(prev => prev.map((v, i) => (toApplyIdx.includes(i) ? false : v)));
      enqueueSnackbar(`Applied ${commands.length} action${commands.length === 1 ? '' : 's'}`, { variant: 'success' });
    } catch (e) {
      console.error('Failed to apply actions', e);
      enqueueSnackbar(`Failed to apply actions: ${e instanceof Error ? e.message : String(e)}`, { variant: 'error' });
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-cm-text-primary">Tidy Suggestions</h1>

      {error && (
        <div className="text-sm text-red-600">{error}</div>
      )}

      {loading || !actions ? (
        <div className="flex justify-center py-8"><LoadingSpinner /></div>
      ) : (
        <Card>
          <Tabs aria-label="Tidy Actions Tabs">
            <TabItem active title={
              <span className="inline-flex items-center gap-2">Duplicates <Badge color="info">{duplicateCount}</Badge></span>
            }>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-sm text-cm-text-primary">Selected: {duplicateSelectedCount}</div>
                    <div>
                      <input
                        type="checkbox"
                        checked={duplicateAllSelected}
                        onChange={(e) => toggleAllDuplicate(e.target.checked)}
                        ref={(el) => { if (el) el.indeterminate = duplicateSomeSelected; }}
                        aria-label="Select all duplicates"
                      />
                      <span className="ml-2 text-xs text-cm-text-secondary">Select all</span>
                    </div>
                  </div>
                  <Button color="failure" size="sm" onClick={deleteSelectedDuplicates} disabled={duplicateSelectedCount === 0}>
                    <FiCheck className="mr-2" /> Delete Selected ({duplicateSelectedCount})
                  </Button>
                </div>

                {/* Duplicates rendered as grid rows; each group on its own row with item cards */}
                <div className="space-y-4">
                  {actions.duplicate_items.map((group, gIdx) => (
                    <div key={gIdx} className="space-y-2">
                      <div className="text-xs uppercase tracking-wide text-cm-text-muted">{group.items.type}</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {group.items.ids.map((id, iIdx) => {
                          const key = `${id.type_}:${id.id}`;
                          const disabled = appliedDuplicateKeys.has(key);
                          const path = id.type_ === 'Task' ? buildUrl({ kind: 'task', taskId: id.id }) : id.type_ === 'Outcome' ? buildUrl({ kind: 'outcome', outcomeId: id.id }) : '#';
                          return (
                            <div key={key} className={`border rounded-md p-3 bg-cm-bg-surface shadow-sm ${disabled ? 'opacity-50' : ''}`}>
                              <div className="flex items-center justify-between">
                                <div className="text-sm font-medium text-cm-text-primary">
                                  <Link to={path} className="text-blue-600 hover:underline">
                                    {id.type_}:{id.id}
                                  </Link>
                                </div>
                                <label className="inline-flex items-center gap-2 text-sm">
                                  <input
                                    type="checkbox"
                                    disabled={disabled}
                                    checked={selectedDuplicateKeys.has(key) && !disabled}
                                    onChange={() => toggleDuplicateItem(key)}
                                  />
                                  <span>Delete</span>
                                </label>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-end">
                  <Button color="failure" size="sm" onClick={deleteSelectedDuplicates} disabled={duplicateSelectedCount === 0}>
                    <FiCheck className="mr-2" /> Delete Selected ({duplicateSelectedCount})
                  </Button>
                </div>
              </div>
            </TabItem>

            <TabItem title={
              <span className="inline-flex items-center gap-2">Remove Completed <Badge color="info">{cleanupCount}</Badge></span>
            }>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-cm-text-primary">Selected: {cleanupSelectedCount}</div>
                  <Button color="green" size="sm" onClick={applyCleanup} disabled={cleanupSelectedCount === 0}>
                    <FiCheck className="mr-2" /> Apply Selected ({cleanupSelectedCount})
                  </Button>
                </div>
                <div className="rounded-md border border-cm-border-subtle">
                  {/* Header */}
                  <div className="bg-cm-bg-elevated text-xs font-medium uppercase tracking-wider grid grid-cols-[40px_1fr_48px] items-center">
                    <div className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={cleanupAllSelected}
                        onChange={(e) => toggleAllCleanup(e.target.checked)}
                        ref={(el) => { if (el) el.indeterminate = cleanupSomeSelected; }}
                        aria-label="Select all"
                      />
                    </div>
                    <div className="px-3 py-2">Node</div>
                    <div className="px-3 py-2 text-right">Info</div>
                  </div>

                  {/* Virtualized body (lightweight) */}
                  {(() => {
                    const items = actions.remove_completed_items;
                    const totalHeight = items.length * rowHeight;
                    const visibleCount = Math.ceil(listHeight / rowHeight) + 4; // buffer
                    const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - 2);
                    const endIndex = Math.min(items.length, startIndex + visibleCount);

                    return (
                      <div
                        style={{ height: listHeight, overflowY: 'auto', overflowX: 'hidden' }}
                        onScroll={(e) => setScrollTop((e.target as HTMLDivElement).scrollTop)}
                      >
                        <div style={{ height: totalHeight, position: 'relative' }}>
                          <div style={{ position: 'absolute', top: startIndex * rowHeight, left: 0, right: 0 }}>
                            {items.slice(startIndex, endIndex).map((a, i) => {
                              const idx = startIndex + i;
                              const disabled = appliedCleanup[idx];
                              return (
                                <div key={idx} className={`grid grid-cols-[40px_1fr_48px] items-start border-t border-cm-border-subtle overflow-hidden ${disabled ? 'opacity-50' : ''}`} style={{ height: rowHeight }}>
                                  <div className="px-3 py-2">
                                    <input
                                      type="checkbox"
                                      disabled={disabled}
                                      checked={!!selectedCleanup[idx] && !disabled}
                                      onChange={() => toggleCleanupRow(idx)}
                                      aria-label={`Select row ${idx + 1}`}
                                    />
                                  </div>
                                  <div className="px-3 py-2 text-sm text-cm-text-primary min-w-0">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <Link
                                        to={a.node_id.type_ === 'Task' ? buildUrl({ kind: 'task', taskId: a.node_id.id }) : a.node_id.type_ === 'Outcome' ? buildUrl({ kind: 'outcome', outcomeId: a.node_id.id }) : '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline truncate"
                                        title={a.node_title}
                                      >
                                        {a.node_title}
                                      </Link>
                                      <span className="text-xs text-cm-text-muted shrink-0">({a.node_id.type_})</span>
                                    </div>
                                  </div>
                                  <div className="px-3 py-2 text-right">
                                    <Tooltip content={
                                      <div className="text-left">
                                        <pre className='text-xs whitespace-pre font-mono leading-5'>
{JSON.stringify(a.suggested_action, null, 2)}
                                        </pre>
                                      </div>
                                    }>
                                      <span className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-cm-bg-hover cursor-help">
                                        <FiInfo />
                                      </span>
                                    </Tooltip>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div className="flex items-center justify-end">
                  <Button color="green" size="sm" onClick={applyCleanup} disabled={cleanupSelectedCount === 0}>
                    <FiCheck className="mr-2" /> Apply Selected ({cleanupSelectedCount})
                  </Button>
                </div>
              </div>
            </TabItem>
          </Tabs>
        </Card>
      )}
    </div>
  );
};

export default TidyPage;
